import { useEffect, useState } from "react";
import { Alert, Button, Form, Select, Spin } from "antd";
import { Input, Checkbox, DatePicker } from "antd";
import { makeApiRequest } from "../../lib/api";
import { getStandardCountryList, extractIdFromExtendedId, formatPhone, WAITLIST } from "../../lib/lib";
import { defaultDataProtection, defaultDefaultCountry, defaultDisclaimer, defaultLinkAGB, defaultLinkDP } from "../../lib/config";
import { TypeParams } from "../../types";
import { useTranslation } from "react-i18next";

const { Option } = Select;

interface Form3RegistrationProps {
  commitRegistration: (response: any) => void;
  returnBackToDates?: () => void;
  returnBackToPlaces?: () => void;
  params: TypeParams; // Define the type of the ccid prop
  placeId: number;
  dateId?: number | string;
}

const Form3Registration: React.FC<Form3RegistrationProps> = ({
  commitRegistration,
  returnBackToDates,
  returnBackToPlaces,
  params,
  placeId,
  dateId,
}) => {
  const { t } = useTranslation();
  const isEgwIntern = params.origin === "egwlink";
  const [form] = Form.useForm();
  const [shortDateId, setShortDateId] = useState<number>();
  const [isWaitlist, setIsWaitlist] = useState(`${shortDateId}` === `${WAITLIST}`);
  const [date, setDate] = useState(null);
  const [place, setPlace] = useState(null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const defaultCountry = params.defaultCountry ? params.defaultCountry : defaultDefaultCountry;
  let dataProtection = params.dataProtection ? params.dataProtection : defaultDataProtection;
  let disclaimer = params.disclaimer ? params.disclaimer : defaultDisclaimer;
  const linkAGB = params.linkAGB ? params.linkAGB : defaultLinkAGB;
  const linkDP = params.linkDP ? params.linkDP : defaultLinkDP;
  const modeWithoutDate = params.page === "infotermin_registration";
  const modeWithDate = params.page !== "infotermin_registration";

  if (isEgwIntern) {
    dataProtection = false;
    disclaimer = false;
  }


  const COUNTRIES = getStandardCountryList(t);
  // FORM 1 SUBMIT: send email or phone number to validate
  const onLoadForm = async () => {
    setError(""); // Clear previous errors
    try {
      // GET ANPASSUNG PLACE
      let action = "";
      let content: any = {};
      action = "REACT_GET_ANPASSUNG_PLACE";
      content = { place_id: placeId };
      const responsePlace: string = (await makeApiRequest(params, action, content)) as any;
      const parsed = JSON.parse(responsePlace);
      if (!parsed || parsed.STATUS !== "OK") {
        setError(t("anpassung.registration.errorLoadingPlace"));
        return;
      }
      setPlace(parsed.CONTENT);

      // GET ANPASSUNG DATE
      if (!isWaitlist && !modeWithoutDate) {
        action = "REACT_GET_ANPASSUNG_DATE";
        content = { date_id: dateId };
        const response = (await makeApiRequest(params, action, content)) as any;
        const parsed_response = JSON.parse(response);
        if (!parsed || parsed.STATUS !== "OK") {
          setError(t("anpassung.registration.errorLoadingDate"));
          return;
        }
        const anpassungDate = parsed_response.CONTENT;
        setDate(anpassungDate);
      } else {
        setDate(null);
      }

      // Set form initial values
      form.setFieldsValue({
        country: defaultCountry,
      });
    } catch (error) {
      console.error(error);
      setError(t("anpassung.registration.genericError"));
    }
  };

  const onFinishFailed = async () => {
    setError(t("anpassung.registration.formIncomplete"));
  };

  const onFinishForm1 = async () => {
    setError(""); // Clear previous errors
    setIsSubmitting(true); // Set loading state
    try {
      const phone1 = formatPhone(form.getFieldValue("phone"));
      const phone2 = formatPhone(form.getFieldValue("phone2"));

      // Prepare customer data
      const customer: any = {
        gender: form.getFieldValue("salutation"),
        n_given: form.getFieldValue("firstname"),
        n_family: form.getFieldValue("lastname"),
        adr_two_street: form.getFieldValue("street"),
        adr_two_postalcode: form.getFieldValue("zip"),
        adr_two_locality: form.getFieldValue("city"),
        tel_home: phone1,
        tel_work: phone2,
        bday: modeWithDate ? form.getFieldValue("bday").format("YYYY-MM-DD") : "",
        promocode: form.getFieldValue("promocode") ? form.getFieldValue("promocode") : "",
        email_home: form.getFieldValue("email").trim().toLowerCase(),
        adr_two_countrycode: form.getFieldValue("country"),
        adr_two_countryname: modeWithDate ? COUNTRIES[form.getFieldValue("country")] : "",
      };

      // Fetch additional form state values
      if (isEgwIntern) {
        customer.data_protection = false;
        customer.disclaimer = false;
      } else {
        customer.data_protection = true;
        customer.disclaimer = true;
      }

      // GET ANPASSUNG PLACE
      let action = "";
      let content: any = {
        place_id: placeId,
        contact: customer,
        triage: {},
      };

      if (isWaitlist) {
        // WAITLIST
        action = "REACT_WAITLIST_REGISTRATION";
      } else if (modeWithoutDate) {
        // CONTACT DATA ONLY REGISTRATION
        action = "REACT_CONTACT_DATA_ONLY_REGISTRATION";
      } else {
        // DEFAULT REGISTRATION
        action = "REACT_ANPASSUNG_REGISTRATION";
        content.ws_id = shortDateId;
        if (isEgwIntern) {
          content.user_email_notification = !!form.getFieldValue("email");
        }
      }

      const responsePlace: string = (await makeApiRequest(params, action, content)) as any;
      const parsed = JSON.parse(responsePlace);
      if (!parsed || parsed.STATUS !== "OK") {
        setError(t("anpassung.registration.errorContactSupport"));
        return;
      }

      commitRegistration(parsed);
    } catch (error) {
      console.error(error);
      setError(t("anpassung.registration.genericError"));
    } finally {
      setIsSubmitting(false); // Reset loading state
    }
  };

  // INIT
  useEffect(() => {
    if (modeWithoutDate) {
      setShortDateId(WAITLIST);
      setIsWaitlist(false);
    } else {
      const localShortDateId: number = extractIdFromExtendedId(`${dateId}`);
      setShortDateId(localShortDateId);
      setIsWaitlist(`${localShortDateId}` === `${WAITLIST}`);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onLoadForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortDateId]);

  if (!place || (!date && !isWaitlist && !modeWithoutDate)) {
    return (
      <div className={"my-lg"}>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      {/* WARTELISTE */}
      {isWaitlist && (
        <>
          <p className={"my-md"}>
            <strong>{t("anpassung.registration.waitlistTitle")}</strong>
          </p>

          <p>
            {t("anpassung.registration.waitlistDescription", {
              placeName: place["name"],
              placeSecondName: place["second_name"] ? `(${place["second_name"]})` : "",
            })}
          </p>
        </>
      )}

      {/* ONLY CONTACT DATA */}
      {params.page === "infotermin_registration" && (
        <>
          <p className={"my-md"}>
            <strong>{t("anpassung.registration.preRegistrationTitle")}</strong>
          </p>

          {/* <p>
            {t("anpassung.registration.preRegistrationDescription", {
              placeName: place["name"],
              placeSecondName: place["second_name"] ? `(${place["second_name"]})` : "",
            })}
          </p> */}
        </>
      )}

      {/* TERMINANMELDUNG */}
      {!isWaitlist && params.page !== "infotermin_registration" && date && (
        <>
          <p className={"my-md"}>
            <strong>{t("anpassung.registration.registrationTitle")}</strong>
          </p>

          <p>
            {t("anpassung.registration.registrationDescription", {
              placeName: place["name"],
              placeSecondName: place["second_name"] ? `(${place["second_name"]})` : "",
              date: date["local_ws_date"],
              time: date["local_ws_time"],
            })}
          </p>
        </>
      )}

      {error && <Alert message="Achtung" description={error} type="error" />}

      <br />

      <div className={"my-lg"}></div>
      <Form
        name="basic"
        // labelCol={{ span: 8 }}
        form={form}
        wrapperCol={{ span: 16 }}
        onFinish={onFinishForm1}
        onFinishFailed={onFinishFailed}
        style={{ maxWidth: 600 }}
        // initialValues={{ remember: true }}
        autoComplete="off"
      >
        <Form.Item
          label={t("anpassung.registration.salutation")}
          name="salutation"
          rules={[{ required: true, message: t("anpassung.registration.salutationError") }]}
        >
          <Select>
            <Option value="f">{t("anpassung.registration.female")}</Option>
            <Option value="m">{t("anpassung.registration.male")}</Option>
            <Option value="o">{t("anpassung.registration.other")}</Option>
          </Select>
        </Form.Item>
        <Form.Item
          label={t("anpassung.registration.firstName")}
          name="firstname"
          rules={[{ required: true, message: t("anpassung.registration.firstNameError") }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={t("anpassung.registration.lastName")}
          name="lastname"
          rules={[{ required: true, message: t("anpassung.registration.lastNameError") }]}
        >
          <Input />
        </Form.Item>
        {modeWithDate && (
          <>
            {" "}
            <Form.Item
              label={t("anpassung.registration.streetNumber")}
              name="street"
              rules={[{ required: true, message: t("anpassung.registration.streetNumberError") }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("anpassung.registration.zip")}
              name="zip"
              rules={[{ required: true, message: t("anpassung.registration.zipError") }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("anpassung.registration.city")}
              name="city"
              rules={[{ required: true, message: t("anpassung.registration.cityError") }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("anpassung.registration.country")}
              name="country"
              rules={[{ required: true, message: t("anpassung.registration.countryError") }]}
            >
              <Select>
                {Object.keys(COUNTRIES).map((code: string) => (
                  <Option key={code} value={code}>
                    {(COUNTRIES as any)[code]}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}

        <Form.Item
          label={t("anpassung.registration.email")}
          name="email"
          rules={!isEgwIntern ? [{ required: !isEgwIntern, message: t("anpassung.registration.emailError") }] : []}
        >
          <Input type="email" />
        </Form.Item>

        {isEgwIntern && <div>{t("anpassung.registration.emailOptional")}</div>}
        <Form.Item
          label={t("anpassung.registration.phone")}
          name="phone"
          rules={[{ required: true, message: t("anpassung.registration.phoneError") }]}
        >
          <Input type="tel" />
        </Form.Item>

        {modeWithDate && (
          <>
            <Form.Item label={t("anpassung.registration.phone2")} name="phone2">
              <Input type="tel" />
            </Form.Item>
            <Form.Item
              label={t("anpassung.registration.birthday")}
              name="bday"
              rules={[{ required: true, message: t("anpassung.registration.birthdayError") }]}
            >
              <DatePicker format={params.dateFormat} />
            </Form.Item>
          </>
        )}

        {params.page !== "infotermin_registration" && (
          <Form.Item label={t("anpassung.registration.promoCode")} name="promocode">
            <Input />
          </Form.Item>
        )}
        {disclaimer && modeWithDate && (
          <>
            <br />
            <Form.Item
              name="disclaimer"
              valuePropName="checked"
              rules={[{ required: true, message: t("anpassung.registration.disclaimerError") }]}
            >
              <Checkbox>
                <span className={"ant-form-item-required"}>
                  <span style={{ color: "red" }}>*</span> {t("anpassung.registration.agbText")}{" "}
                  <a href={linkAGB} target="_blank" rel="noreferrer">
                    {t("anpassung.registration.agbLink")}
                  </a>{" "}
                  {t("anpassung.registration.agbConfirmation")}
                </span>
              </Checkbox>
            </Form.Item>
          </>
        )}
        {dataProtection && (
          <Form.Item
            name="dataProtection"
            valuePropName="checked"
            rules={[{ required: true, message: t("anpassung.registration.dataProtectionError") }]}
          >
            <Checkbox>
              <span>
                <>
                  <span style={{ color: "red" }}>*</span> {t("anpassung.registration.dataProtectionText")}{" "}
                  <a href={linkDP} target="_blank" rel="noreferrer">
                    {t("anpassung.registration.dataProtectionLink")}
                  </a>{" "}
                  {t("anpassung.registration.dataProtectionConfirmation")}
                </>
              </span>
            </Checkbox>
          </Form.Item>
        )}
        <Form.Item name="ext_ws_id" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="ext_place_id" hidden>
          <Input />
        </Form.Item>
        <br />
        <div>* {t("anpassung.registration.requiredFields")}</div>
        <br />
        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <div>
            {returnBackToDates && (
              <Button type="link" onClick={returnBackToDates}>
                {t("anpassung.registration.back")}
              </Button>
            )}
            {returnBackToPlaces && (
              <Button type="link" onClick={returnBackToPlaces}>
                {t("anpassung.registration.back")}
              </Button>
            )}

            <Button type="primary" htmlType="submit" loading={isSubmitting}>
              {t("anpassung.registration.submit")}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default Form3Registration;
